import optionsApi from "./optionsApi";
import {
  BulkGrantIdUpdateResponse,
  GrantIdUpdateRow,
  ParsedTransactionRow,
  TransactionValidationResult,
  UpdateTransactionsResponse,
} from "../types/BulkGrantUpdate";

/**
 * Download the grant transaction template for bulk update
 * @param grantIds - Optional array of grant IDs to include in template
 * @returns Blob of the Excel file
 */
export const getTransactionTemplate = async (
  grantIds?: string[]
): Promise<Blob> => {
  const response = await optionsApi.post(
    "v1/grantTransactions/template",
    { grantIds },
    {
      responseType: "blob",
    }
  );
  return response.data;
};

/**
 * Parse and validate the uploaded transaction file
 * @param file - Base64 encoded file content
 * @returns Validation result with parsed rows
 */
export const parseTransactionUpload = async (
  file: string
): Promise<TransactionValidationResult> => {
  const response = await optionsApi.post<{
    data: TransactionValidationResult;
  }>("v1/grantTransactions/parse", { file });
  return response.data.data;
};

/**
 * Update grant transactions in bulk
 * @param transactions - Array of parsed transaction rows to update
 * @returns Update result with created count and any errors
 */
export const updateGrantTransactions = async (
  transactions: ParsedTransactionRow[]
): Promise<UpdateTransactionsResponse> => {
  const response = await optionsApi.post<{
    data: UpdateTransactionsResponse;
  }>("v1/grantTransactions/update", { transactions });
  return response.data.data;
};

/**
 * Validate grant transactions in bulk
 * @param transactions - Array of parsed transaction rows to validate
 * @returns Validation result with parsed rows and errors
 */
export const validateTransactions = async (
  transactions: ParsedTransactionRow[]
): Promise<TransactionValidationResult> => {
  const response = await optionsApi.post<{
    data: TransactionValidationResult;
  }>("v1/grantTransactions/validate", { transactions });
  return response.data.data;
};

export const bulkUpdateGrantIds = async (
  updates: GrantIdUpdateRow[]
): Promise<BulkGrantIdUpdateResponse> => {
  const response = await optionsApi.post<{
    data: BulkGrantIdUpdateResponse;
  }>("v1/grant/bulk-update-ids", { updates });
  return response.data.data;
};

export const downloadGrantIdTemplate = async (
  selectedGrantIds: string[] = []
): Promise<Blob> => {
  const response = await optionsApi.post(
    "v1/grant/bulk-update-ids/template",
    { selectedGrantIds },
    { responseType: "blob" }
  );
  return response.data;
};

export const parseGrantIdUpdateUpload = async (
  file: string
): Promise<GrantIdUpdateRow[]> => {
  const response = await optionsApi.post<{ data: GrantIdUpdateRow[] }>(
    "v1/grant/bulk-update-ids/parse",
    { file }
  );
  return response.data.data;
};
